import React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Filter, X } from 'lucide-react';
import { TicketPriority, TicketFilter, EMPTY_TICKET_FILTER } from '@/types/tickets';
import { isFilterActive } from '@/lib/ticket-filter';

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  [TicketPriority.URGENT]: 'Urgente',
  [TicketPriority.HIGH]: 'Alta',
  [TicketPriority.MEDIUM]: 'Média',
  [TicketPriority.LOW]: 'Baixa',
};

interface TicketFilterMenuProps {
  filter: TicketFilter;
  onChange: (filter: TicketFilter) => void;
  /** Ação do "Limpar filtros". Default: zera apenas este filtro. */
  onClear?: () => void;
  /** Força a exibição do "Limpar filtros". Default: visível quando este filtro está ativo. */
  clearVisible?: boolean;
  align?: 'start' | 'end';
}

const TicketFilterMenu: React.FC<TicketFilterMenuProps> = ({
  filter,
  onChange,
  onClear,
  clearVisible,
  align = 'end',
}) => {
  const active = isFilterActive(filter);
  const showClear = clearVisible ?? active;
  const handleClear = onClear ?? (() => onChange(EMPTY_TICKET_FILTER));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={active ? 'default' : 'outline'}
          size="sm"
          className="h-8 w-8 p-0"
        >
          <Filter className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <DropdownMenuLabel>Filtrar por</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => onChange({ ...filter, assignedToMe: !filter.assignedToMe })}
        >
          <div className="flex items-center justify-between w-full">
            <span>Atribuído para mim</span>
            {filter.assignedToMe && <span className="text-xs">✓</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel>Prioridade</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onChange({ ...filter, priority: 'all' })}>
          <div className="flex items-center justify-between w-full">
            <span>Todas</span>
            {filter.priority === 'all' && <span className="text-xs">✓</span>}
          </div>
        </DropdownMenuItem>
        {Object.entries(PRIORITY_LABELS).map(([priority, label]) => (
          <DropdownMenuItem
            key={priority}
            onClick={() =>
              onChange({
                ...filter,
                priority:
                  filter.priority === (priority as TicketPriority)
                    ? null
                    : (priority as TicketPriority),
              })
            }
          >
            <div className="flex items-center justify-between w-full">
              <span>{label}</span>
              {filter.priority === priority && <span className="text-xs">✓</span>}
            </div>
          </DropdownMenuItem>
        ))}

        {showClear && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleClear} className="text-destructive">
              <X className="h-4 w-4 mr-2" />
              Limpar filtros
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TicketFilterMenu;
